import React, { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  priority?: boolean;
  onLoad?: () => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  blurhash?: string;
  sizes?: string;
}

// Global image load tracking for better UX feedback
const globalLoadTracker = {
  total: 0,
  loaded: 0,
  failed: 0
};

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  objectFit = 'cover',
  priority = false,
  onLoad,
  onError,
  blurhash,
  sizes = '100vw'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imageRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Track this image in the global tracker
  useEffect(() => {
    // Only track images that will be loaded
    if (src && (priority || isInView)) {
      globalLoadTracker.total += 1;
      
      return () => {
        // Decrease the counter when component unmounts before loading completes
        if (!isLoaded && !error) {
          globalLoadTracker.total -= 1;
        }
      };
    }
  }, [src, priority, isInView]);
  
  // Detect browser WebP support
  const supportsWebP = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const elem = document.createElement('canvas');
    if (elem.getContext && elem.getContext('2d')) {
      // WebP detection
      return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    return false;
  }, []);
  
  // Detect browser AVIF support
  const supportsAVIF = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if ('HTMLPictureElement' in window && window.navigator.userAgent.indexOf('Chrome') > -1) {
      return true; // Chrome supports AVIF
    }
    return false;
  }, []);

  // Generate optimized version of the image URL
  const optimizedSrc = useMemo(() => {
    // Skip optimization for data URLs, SVGs or relative URLs
    if (
      !src || 
      src.startsWith('data:') || 
      src.startsWith('/') ||
      src.endsWith('.svg')
    ) {
      return src;
    }
    
    try {
      // Try to create a URL object to validate the URL
      const url = new URL(src);
      
      // Check if URL is from known optimization-friendly services
      if (url.hostname.includes('unsplash.com')) {
        // Add image optimization parameters for Unsplash
        const format = supportsAVIF ? 'avif' : supportsWebP ? 'webp' : 'jpg';
        return `${src}${src.includes('?') ? '&' : '?'}q=80&fm=${format}&w=${width || getResponsiveWidth()}&fit=crop`;
      }
      
      if (url.hostname.includes('images.pexels.com')) {
        // Pexels allows direct size manipulation
        const format = supportsWebP ? 'webp' : 'compress';
        return `${src}?auto=${format}&w=${width || getResponsiveWidth()}`;
      }

      if (url.hostname.includes('cloudinary.com')) {
        // Cloudinary optimization
        const format = supportsAVIF ? 'avif' : supportsWebP ? 'webp' : 'auto';
        return src.replace('/upload/', `/upload/q_auto,f_${format},w_${width || getResponsiveWidth()}/`);
      }

      if (url.hostname.includes('res.cloudinary.com')) {
        // Another cloudinary pattern
        const format = supportsAVIF ? 'avif' : supportsWebP ? 'webp' : 'auto';
        return src.replace('/image/upload/', `/image/upload/q_auto,f_${format},w_${width || getResponsiveWidth()}/`);
      }

      if (url.hostname.includes('googleusercontent.com') && src.includes('=')) {
        // Google storage often uses =s{size} format
        return src.replace(/=s\d+/, `=s${width || getResponsiveWidth()}`);
      }
      
      // For other URLs, return as is for now
      return src;
    } catch (e) {
      // If URL parsing fails, return the original src
      return src;
    }
  }, [src, width, supportsWebP, supportsAVIF]);

  // Get responsive width based on viewport with device pixel ratio consideration
  function getResponsiveWidth(): number {
    if (typeof window === 'undefined') return 800;
    const viewportWidth = window.innerWidth;
    const dpr = window.devicePixelRatio || 1;
    
    // Responsive widths with device pixel ratio factored in
    if (viewportWidth <= 640) return Math.round(640 * dpr); 
    if (viewportWidth <= 1024) return Math.round(1024 * dpr);
    return Math.round(1600 * dpr); 
  }

  // Dynamically set aspect ratio based on dimensions
  const aspectRatio = useMemo(() => {
    if (width && height) {
      return width / height;
    }
    return undefined;
  }, [width, height]);

  useEffect(() => {
    if (priority) return; // Skip observation for priority images
    
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (observerRef.current && imageRef.current) {
            observerRef.current.unobserve(imageRef.current);
          }
        }
      },
      {
        rootMargin: '300px', // Increased from 200px for more aggressive prefetching
        threshold: 0.01
      }
    );

    if (imageRef.current && observerRef.current) {
      observerRef.current.observe(imageRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, priority]);

  // Preload higher priority images
  useEffect(() => {
    if (priority && optimizedSrc) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = optimizedSrc;
      document.head.appendChild(preloadLink);
      
      return () => {
        document.head.removeChild(preloadLink);
      };
    }
  }, [optimizedSrc, priority]);

  // Handle image load and error cases
  const handleImageLoad = () => {
    setIsLoaded(true);
    globalLoadTracker.loaded += 1;
    if (onLoad) onLoad();
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError(true);
    setIsLoaded(true);
    globalLoadTracker.failed += 1;
    if (onError) onError(e);
  };

  // Generate a unique ID from the image source for debugging
  const imageId = useMemo(() => `img-${src.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '')}`, [src]);

  // Advanced picture element rendering with multiple formats
  if ((isInView || priority) && !error) {
    return (
      <div 
        id={imageId}
        ref={imageRef}
        className={cn(
          "relative overflow-hidden", 
          className
        )}
        style={{ 
          width: width ? `${width}px` : '100%', 
          height: height ? `${height}px` : undefined,
          aspectRatio
        }}
      >
        {!isLoaded && (
          <Skeleton 
            className={cn(
              "absolute inset-0 z-10",
              width ? `w-${width}` : "w-full",
              height ? `h-${height}` : "h-full"
            )} 
          />
        )}
        
        {/* Use picture element for better browser format support */}
        <picture>
          {/* AVIF support for Chrome */}
          {supportsAVIF && (
            <source
              srcSet={optimizedSrc.replace(/fm=webp|fm=jpg|fm=auto/, 'fm=avif')}
              type="image/avif"
            />
          )}
          
          {/* WebP support */}
          {supportsWebP && (
            <source
              srcSet={optimizedSrc.replace(/fm=jpg|fm=auto/, 'fm=webp')}
              type="image/webp"
            />
          )}
          
          {/* Fallback image - Fixed fetchPriority to camelCase for TypeScript */}
          <img 
            src={optimizedSrc}
            alt={alt}
            width={width}
            height={height}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            onLoad={handleImageLoad}
            onError={handleImageError}
            sizes={sizes}
            className={cn(
              "transition-opacity duration-300",
              isLoaded ? "opacity-100" : "opacity-0",
              `object-${objectFit}`,
              "w-full h-full"
            )}
          />
        </picture>
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground text-sm">
            <span>Failed to load image</span>
          </div>
        )}
      </div>
    );
  }

  // Return placeholder while not in view
  return (
    <div 
      ref={imageRef}
      className={cn(
        "relative overflow-hidden bg-muted", 
        className
      )}
      style={{ 
        width: width ? `${width}px` : '100%', 
        height: height ? `${height}px` : undefined,
        aspectRatio: aspectRatio
      }}
    />
  );
};

// Export the global load tracker for debugging
export const imageLoadStats = globalLoadTracker;

export default OptimizedImage;
